import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { aabb, type SceneStore } from '@os/doc';
import { unionRects, type Rect } from '@os/engine';
import type { EditorController } from './controller';
import { MINIMAP_SIZE, minimapToWorld, minimapTransform } from './minimap-geometry';
import { SectionList } from './SectionList';
import { ListItem } from '../../ui';
import { DEFAULT_STICKY } from '../../ui/palette';
import { FLOATING_BAR } from './canvas-ui';

const COLORS: Record<string, string> = { image: '#a1a1aa', sticky: DEFAULT_STICKY, section: '#e4e4e7', text: '#52525b', link: '#93c5fd', embed: '#1f2937', table: '#d4d4d8' };
const OPEN_KEY = 'os.minimap.open';
const readOpen = () => {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0';
  } catch {
    return true;
  }
};

/**
 * Board overview: object bounds and the current view, redrawn at most once per frame; click or drag to move the view.
 * Lists the sections below it. `raf` and `onDraw` are test hooks.
 */
/** Open or folded, remembered on this device; the editor owns it so the main menu can toggle it (P5d). */
export function useMinimapOpen(): [boolean, () => void] {
  const [open, setOpen] = useState(readOpen);
  const toggle = useCallback(
    () =>
      setOpen((v) => {
        try {
          localStorage.setItem(OPEN_KEY, v ? '0' : '1');
        } catch {
          // per-viewer convenience only
        }
        return !v;
      }),
    [],
  );
  return [open, toggle];
}

export function Minimap({
  store,
  controller,
  raf = requestAnimationFrame,
  onDraw,
  state,
}: {
  store: SceneStore;
  controller: EditorController;
  raf?: (cb: () => void) => number;
  onDraw?: () => void;
  /** From `useMinimapOpen` in the owner; the minimap keeps its own otherwise. */
  state?: [boolean, () => void];
}) {
  const { t } = useTranslation();
  const own = useMinimapOpen();
  const [open, toggle] = state ?? own;
  const canvas = useRef<HTMLCanvasElement>(null);
  const transform = useRef(minimapTransform(null, controller.viewRect()));
  const dragging = useRef(false);

  useEffect(() => {
    if (!open) return;
    let pending = false;
    const draw = () => {
      pending = false;
      const g = store.graph();
      const rects = store.list().filter((o) => !g.hidden.has(o.id) && g.world.has(o.id)).map((o) => ({ type: o.type, r: aabb(g.world.get(o.id)!) }));
      const view = controller.viewRect();
      const t = minimapTransform(unionRects(rects.map((x) => x.r)), view);
      transform.current = t;
      onDraw?.();
      const ctx = canvas.current?.getContext('2d');
      if (!ctx) return;
      const box = (r: Rect) => [r.minX * t.scale + t.dx, r.minY * t.scale + t.dy, Math.max(1, (r.maxX - r.minX) * t.scale), Math.max(1, (r.maxY - r.minY) * t.scale)] as const;
      ctx.clearRect(0, 0, MINIMAP_SIZE.w, MINIMAP_SIZE.h);
      for (const { type, r } of rects) {
        ctx.fillStyle = COLORS[type] ?? '#71717a';
        ctx.fillRect(...box(r));
      }
      ctx.strokeStyle = '#d90429';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(...box(view));
    };
    const schedule = () => {
      if (pending) return;
      pending = true;
      raf(draw);
    };
    schedule();
    const offs = [store.subscribeList(schedule), controller.subscribeCamera(schedule)];
    return () => offs.forEach((off) => off());
  }, [open, store, controller, raf, onDraw]);

  const moveTo = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    controller.centerOn(minimapToWorld(transform.current, { x: e.clientX - r.left, y: e.clientY - r.top }));
  };
  return (
    <aside aria-label={t('minimap.label')} data-fit-corner="" className={`absolute bottom-[4.5rem] right-4 z-10 w-[200px] overflow-hidden rounded-2xl font-sans text-xs text-fg ${FLOATING_BAR} max-sm:hidden`}>
      <ListItem
        aria-expanded={open}
        onClick={toggle}
        label={t('minimap.label')}
        hint={<span aria-hidden="true">{open ? '▾' : '▸'}</span>}
        className="h-auto rounded-none px-2.5 py-1.5 text-xs font-medium text-fg-muted hover:bg-transparent hover:text-fg focus-visible:-outline-offset-2 pointer-coarse:h-auto pointer-coarse:min-h-11"
      />
      {open && (
        <>
          <canvas
            ref={canvas}
            width={MINIMAP_SIZE.w}
            height={MINIMAP_SIZE.h}
            role="img"
            aria-label={t('minimap.map')}
            className="block cursor-pointer bg-paper"
            onPointerDown={(e) => {
              dragging.current = true;
              e.currentTarget.setPointerCapture?.(e.pointerId);
              moveTo(e);
            }}
            onPointerMove={(e) => dragging.current && moveTo(e)}
            onPointerUp={() => (dragging.current = false)}
          />
          <SectionList store={store} controller={controller} />
        </>
      )}
    </aside>
  );
}
